import React from "react";
import Header from "../components/header";
import Sidebar from "../components/sidebar";
import { Toaster } from "@shadcn-in-nx/ui/sonner";
import { useAuth } from "../context/AuthContext";
import useStorage from "../hooks/useStorage";
import { useBluelagoon } from "@bluelagoon";
import { useTranslation } from "react-i18next";
import { Navigate } from "react-router-dom";
import { LayoutProvider } from "../context/LayoutContext";

const MainLayout = ({
    children,
    headerChildren,
    leftHeaderChildren,
    noPadding,
    noHeader,
}) => {
    const { t, i18n } = useTranslation();
    const [sidebarOpen, setSidebarOpen] = useStorage(
        "sidebarOpen",
        window.innerWidth < 768 ? "false" : "true",
        window.localStorage
    );
    const sideBarOpenBoolean = sidebarOpen === "true";

    const {
        call: insertNewStripeSession,
        error: stripeError,
        isLoading: stripeLoading,
    } = useBluelagoon({
        method: "insertNewStripeSession",
    });

    const handleBillingClick = async () => {
        const data = await insertNewStripeSession();

        if (data && data.data && data.data.sessionUrl) {
            window.location.href = data.data.sessionUrl;
        }
    };

    const toggleSidebar = React.useCallback(
        () => setSidebarOpen((prev) => (prev === "true" ? "false" : "true")),
        []
    );

    const { user, isClient, isSupport, isSuperAdmin, isFeatureEnabled } = useAuth();

    const allOnboardingComplete = React.useMemo(() => {
        const keys = ["onboarding-create-workflow", "onboarding-best-practices", "onboarding-credits-usage", "onboarding-add-team-member", "onboarding-add-brand"];
        return keys.every(k => user?.tutorials?.[k]?.completed);
    }, [user?.tutorials]);

    const MENU = React.useMemo(
        () => [
            {
                label: t("layout.menu.mainMenu"),
                section: true,
            },
            // {
            //     label: t("layout.menu.getStarted"),
            //     route: "/v2/get-started",
            //     icon: "Rocket",
            //     className:
            //         "bg-gradient-to-r from-purple-600 via-pink-600 to-yellow-600 hover:from-purple-700 hover:via-pink-700 hover:to-yellow-700 text-white rounded-lg text-lg font-medium transition-all duration-200",
            // },
            {
                label: t("layout.menu.chat"),
                route: "/chat",
                icon: "MessageCircle",
            },
            {
                label: t("layout.menu.myProjects"),
                route: "/workflows",
                icon: "LayoutGrid",
            },
            // {
            //     label: "Chat Assistant",
            //     route: "/chat",
            //     icon: "MessageSquare",
            // },
            {
                label: t("layout.menu.connectApps"),
                route: "/integrate",
                icon: "ToyBrick",
                alertBadge: "integrationAlerts",
            },
            {
                label: t("layout.menu.skills"),
                route: "/skills",
                icon: "Brain",
            },
            {
                label: t("layout.menu.blueprints", "Blueprints"),
                route: "/blueprints",
                icon: "Package",
            },
            {
                label: t("layout.menu.yourData"),
                route: "/databases",
                icon: "Table",
            },
            {
                label: t("layout.menu.files", "Files"),
                route: "/files",
                icon: "Files",
            },
            {
                label: t("layout.menu.secrets"),
                route: "/secrets",
                icon: "Key",
            },
            {
                label: t("layout.menu.monitoring"),
                route: "/v2/monitoring",
                icon: "Clock",
            },
            {
                label: t("layout.menu.clientPortal", "Internal Portal"),
                route: "/client/workflows",
                icon: "Users",
                restricted: !isFeatureEnabled("clientPortal"),
            },
            {
                label: t("layout.menu.learnHere"),
                route: "https://help.agentui.ai",
                target: "_blank",
                icon: "BookMarked",
            },

            {
                label: t("layout.menu.support", "Support"),
                icon: "LifeBuoy",
                route: "/support",
            },
            ...(!allOnboardingComplete
                ? [{
                      label: t("layout.menu.getStarted", "Get Started"),
                      route: "/onboarding",
                      icon: "Rocket",
                      className:
                          "onboarding-glow-btn bg-white text-green-700 hover:bg-green-50 font-medium rounded-full",
                  }]
                : []),
            {
                label: t("layout.menu.settings"),
                section: true,
            },
            {
                label: t("layout.menu.profileAndUsers"),
                route: "/profile",
                icon: "Users",
            },

            ...(user?.roles?.includes("admin") ||
            user?.roles?.includes("super-admin") ||
            user?.roles?.includes("owner")
                ? [
                      {
                          label: t("layout.menu.billing"),
                          icon: "CreditCard",
                          route: "/billing",
                      },
                  ]
                : []),
            ...(isSuperAdmin || isSupport
                ? [
                      {
                          label: t("layout.menu.admin"),
                          route: "/admin",
                          icon: "Shield",
                      },
                  ]
                : []),
        ],
        [user, t, isFeatureEnabled, allOnboardingComplete]
    );

    if (isClient) {
        return <Navigate to="/client/workflows" />;
    }

    return (
        <>
            <div className="h-screen flex flex-col">
                {noHeader ? null : (
                    <Header
                        toggleSidebar={toggleSidebar}
                        sidebarOpen={sideBarOpenBoolean}
                        headerChildren={headerChildren}
                        leftHeaderChildren={leftHeaderChildren}
                    >
                        {headerChildren}
                    </Header>
                )}
                <div className="flex flex-1 relative overflow-hidden min-h-0 hide-for-print">
                    <Sidebar
                        isOpen={sideBarOpenBoolean}
                        menu={MENU}
                        setIsOpen={toggleSidebar}
                        user={user}
                    />
                    <main
                        className={`flex-1 ${
                            noPadding ? "" : "p-2 md:p-3"
                        } overflow-y-auto transition-all duration-300 ${
                            sideBarOpenBoolean ? "lg:ml-64" : "lg:ml-[68px]"
                        }`}
                    >
                        <LayoutProvider
                            toggleSidebar={toggleSidebar}
                            sidebarOpen={sideBarOpenBoolean}
                        >
                            {children}
                        </LayoutProvider>
                    </main>
                </div>
            </div>
            <Toaster position="top-right" />
        </>
    );
};

export default MainLayout;
